<script setup lang="ts">
import { computed } from 'vue';
import { useToast } from '../composables/useToast';

const { toasts, dismiss } = useToast();

const polite = computed(() =>
  toasts.value
    .filter((toast) => toast.tone !== 'error')
    .map((toast) => toast.message)
    .join('. '),
);

const assertive = computed(() =>
  toasts.value
    .filter((toast) => toast.tone === 'error')
    .map((toast) => toast.message)
    .join('. '),
);
</script>

<template>
  <div class="toasts" role="region" aria-label="Notifications">
    <p class="sr-only" role="status" aria-live="polite">{{ polite }}</p>
    <p class="sr-only" role="alert" aria-live="assertive">{{ assertive }}</p>

    <TransitionGroup name="toast">
      <div v-for="toast in toasts" :key="toast.id" :class="['toast', `toast--${toast.tone}`]">
        <span class="toast__text" aria-hidden="true">{{ toast.message }}</span>
        <button
          type="button"
          class="toast__close"
          :aria-label="`Dismiss: ${toast.message}`"
          @click="dismiss(toast.id)"
        >
          ×
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<style scoped>
.toasts {
  position: fixed;
  right: var(--space-6);
  bottom: var(--space-6);
  z-index: var(--z-toast);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: min(380px, calc(100vw - var(--space-8)));
  pointer-events: none;
}

.toast {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-4);
  background: var(--glass-bg);
  border: 1px solid var(--glass-border);
  border-left: 3px solid var(--info-500);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  backdrop-filter: blur(var(--glass-blur)) saturate(160%);
  -webkit-backdrop-filter: blur(var(--glass-blur)) saturate(160%);
  font-size: var(--text-sm);
  pointer-events: auto;
}

.toast--success {
  border-left-color: var(--success-500);
}

.toast--error {
  border-left-color: var(--danger-500);
}

.toast__text {
  flex: 1;
  color: var(--text-primary);
}

.toast__close {
  flex-shrink: 0;
  padding: 0 var(--space-1);
  font-size: var(--text-lg);
  line-height: 1;
  color: var(--text-muted);
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.toast__close:hover {
  color: var(--text-primary);
}

.toast-enter-active,
.toast-leave-active {
  transition:
    opacity var(--duration-normal) var(--ease-out),
    transform var(--duration-normal) var(--ease-spring);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(24px) scale(0.96);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(24px);
}

@media (max-width: 640px) {
  .toasts {
    right: var(--space-4);
    left: var(--space-4);
    bottom: var(--space-4);
    width: auto;
  }
}
</style>
